/**
 * @component ry-number-select
 * @category  form
 * @summary   Numeric stepper with buttons, drag-to-change, keyboard, wheel, and optional inline-edit (ry-ui-only). A single value clamped between `[min, max]` snapped to `[step]`, optionally wrapping at edges. Buttons hold-to-repeat with acceleration. Drag the display (horizontal or vertical) to adjust by step; Shift increases step ×10. Double-click (or Enter with `[editable]`) opens an inline text input.
 *
 * @attribute min        number?   default=0     Minimum value.
 * @attribute max        number?   default=100   Maximum value.
 * @attribute step       number?   default=1     Step increment. Drives keyboard/wheel/drag step + display formatting (decimals derived from step).
 * @attribute value      number?   default=0     Current value. Clamped on every change; `[wrap]` wraps instead of clamping.
 * @attribute arrows     'both' | 'start' | 'end' | 'none' | 'stacked' | 'stacked-end' | 'stacked-start' ?  default="both"   Where buttons render. `none` hides them; `stacked` arranges vertically with display between.
 * @attribute icons      'plus-minus' | 'chevron' | 'arrow' ?  default="plus-minus"   Glyph set for the buttons. Resolved from the icon registry.
 * @attribute drag       'x' | 'y' | 'none' ?    Drag axis. Default: `x` for horizontal layouts, `y` for stacked layouts. `none` disables drag.
 * @attribute wrap       boolean   default=false   Values past `max` wrap to `min` (and vice versa) instead of clamping. Useful for hour/minute pickers.
 * @attribute editable   boolean   default=false   Enables inline text edit on double-click / Enter / typing a digit. Without this, the value is button-/drag-/keyboard-only.
 * @attribute label      string?              Accessible label rendered alongside the display + injected as `aria-label`.
 * @attribute prefix     string?              Inline text before the value (e.g. `"$"`).
 * @attribute suffix     string?              Inline text after the value (e.g. `"%"`, `"px"`).
 * @attribute disabled   boolean   default=false   Non-interactive.
 *
 * @property  value      number    Live numeric value (parsed from attribute, NOT clamped at read — callers should `min`/`max`-check).
 * @property  min        number    Live numeric min.
 * @property  max        number    Live numeric max.
 * @property  step       number    Live numeric step.
 * @property  drag       'x' | 'y' | 'none'   Resolved drag axis.
 * @property  disabled   boolean   Live disabled state.
 *
 * @event ry:input
 *        detail: { value: number }
 *        fires:  on micro-step during drag, on button repeat ticks, on every keyboard step. Use for live previews.
 *
 * @event ry:beforechange
 *        detail: { value: number, previous: number }
 *        fires:  before each commit — button press, keyboard step, wheel step, edit commit, drag release — with `value` the number ABOUT to be committed and `previous` the current value (for drag release: the value at drag start). Not fired for `ry:input` streams (drag frames, button repeat ticks).
 *        cancelable: preventDefault() keeps `previous` — a vetoed drag release snaps the display back to the pre-drag value — and `ry:change` does not fire.
 *
 * @event ry:change
 *        detail: { value: number }
 *        fires:  on button initial press, on drag release (with movement), on every keyboard step, on edit commit. Use for committed values. Keyboard fires both ry:input AND ry:change for each keystroke (no separate "release" event for keyboard).
 *
 * @keyboard  Display has `tabindex="0"`. ArrowUp/ArrowRight — increment by step. ArrowDown/ArrowLeft — decrement. Shift+Arrow — 10× step. PageUp/PageDown — 10× step. Home/End — jump to min/max. Enter — enter edit mode (when `[editable]`). Typing 0-9, ., or - while focused — enter edit mode prefilled with that key (when `[editable]`).
 *
 * @state     `data-ry-state` = `"editing"` while inline edit input is active. `data-dragging` present on the host during an active drag.
 *
 * @content   None — `<ry-number-select>` is configured entirely via attributes. innerHTML is replaced at setup.
 *
 * @dom
 *   ry-number-select[min][max][step][value][...]
 *     > button.ry-number-select__btn.ry-number-select__decrement   [data-ry-target="decrement"]   (unless arrows="end" or "none")
 *     > div.ry-number-select__display    [data-ry-target="display"]   role="spinbutton"  tabindex="0"  aria-valuenow=value
 *       > span.ry-number-select__affix   [data-ry-target="prefix"]   (only when [prefix])
 *       > span.ry-number-select__value   [data-ry-target="value"]
 *       > span.ry-number-select__affix   [data-ry-target="suffix"]   (only when [suffix])
 *       > input.ry-number-select__input  [data-ry-target="input"]  type="text"  inputmode="decimal"   (hidden until edit mode)
 *     > button.ry-number-select__btn.ry-number-select__increment   [data-ry-target="increment"]   (unless arrows="start" or "none")
 *     > span.ry-number-select__label     [data-ry-target="label"]   (only when [label])
 *
 * @gotcha  Keyboard fires BOTH `ry:input` AND `ry:change` on every keystroke (since there's no separate "release" for keyboard, unlike pointer drag). For input-only debouncing, listen to `ry:input` and ignore `ry:change`. For commit-only patterns, debounce `ry:change` yourself.
 *
 * @gotcha  Button hold-to-repeat has acceleration: 400ms initial delay, then ticks at 150ms decaying toward 40ms. The FIRST press fires `ry:change`; subsequent repeat ticks fire `ry:input` only. Releasing the button stops the timer — no separate stop event.
 *
 * @gotcha  Drag axis defaults to `y` for stacked layouts and `x` otherwise. Set `[drag="none"]` to disable drag entirely without removing buttons.
 *
 * @gotcha  `[wrap]` semantics: values past `max` wrap to `min`. The wrap formula uses `max - min + step` as the range size — for a 0..10 step=1 wrap, the cycle is 0,1,...,10,0,1,... (11 distinct values). Adjust step so the wrap lands where you want.
 *
 * @gotcha  Wheel events only adjust when the element is hovered OR contains focus. This prevents wheel scrolling on the page from accidentally changing values when the cursor is far away. happy-dom doesn't simulate `:hover` — to test wheel, focus the display first.
 *
 * @gotcha  Drag interactions require real pointer coordinates + getBoundingClientRect. happy-dom can't simulate them; vitest covers buttons/keyboard/wheel/edit only.
 *
 * @example basic
 * ```html
 * <ry-number-select min="0" max="100" value="50"></ry-number-select>
 * ```
 *
 * @example with-step
 * ```html
 * <ry-number-select min="0" max="10" step="0.5" value="2.5"></ry-number-select>
 * ```
 *
 * @example stacked-buttons
 * ```html
 * <ry-number-select min="0" max="100" value="50" arrows="stacked"></ry-number-select>
 * ```
 *
 * @example with-prefix-suffix
 * ```html
 * <ry-number-select min="0" max="100" value="42" prefix="$" suffix=" USD"></ry-number-select>
 * ```
 *
 * @example editable
 * ```html
 * <ry-number-select min="0" max="100" value="42" editable></ry-number-select>
 * ```
 *
 * @example wrap-hour-picker
 * ```html
 * <ry-number-select min="0" max="23" value="9" wrap></ry-number-select>
 * ```
 *
 * @example chevron-icons
 * ```html
 * <ry-number-select min="0" max="10" value="3" icons="chevron"></ry-number-select>
 * ```
 *
 * @tutorial mount-renders-display-and-buttons
 * ```js
 * const el = mount('<ry-number-select min="0" max="100" value="50"></ry-number-select>');
 * await expectEqual(!!el.querySelector('[data-ry-target="display"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="decrement"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="increment"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="value"]'), true);
 * ```
 *
 * @tutorial arrows-end-omits-decrement
 * ```js
 * const el = mount('<ry-number-select min="0" max="10" value="5" arrows="end"></ry-number-select>');
 * await expectEqual(el.querySelector('[data-ry-target="decrement"]'), null, 'no decrement when arrows="end"');
 * await expectEqual(!!el.querySelector('[data-ry-target="increment"]'), true);
 * ```
 *
 * @tutorial arrows-none-omits-both-buttons
 * ```js
 * const el = mount('<ry-number-select min="0" max="10" value="5" arrows="none"></ry-number-select>');
 * await expectEqual(el.querySelector('[data-ry-target="decrement"]'), null);
 * await expectEqual(el.querySelector('[data-ry-target="increment"]'), null);
 * ```
 *
 * @tutorial prefix-and-suffix-render
 * ```js
 * const el = mount('<ry-number-select prefix="$" suffix=" USD" min="0" max="100" value="42"></ry-number-select>');
 * await expectEqual(el.querySelector('[data-ry-target="prefix"]')?.textContent, '$');
 * await expectEqual(el.querySelector('[data-ry-target="suffix"]')?.textContent, ' USD');
 * ```
 *
 * @tutorial keyboard-arrow-increments-and-fires-events
 * ```js
 * const el = mount('<ry-number-select min="0" max="100" step="5" value="50"></ry-number-select>');
 * const fired = { input: [], change: [] };
 * el.addEventListener('ry:input', (e) => fired.input.push(e.detail.value));
 * el.addEventListener('ry:change', (e) => fired.change.push(e.detail.value));
 * const display = el.querySelector('[data-ry-target="display"]');
 * display.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowUp', bubbles: true }));
 * await expectEqual(el.value, 55);
 * await expectEqual(fired.input[0], 55);
 * await expectEqual(fired.change[0], 55);
 * ```
 *
 * @tutorial shift-arrow-multiplies-by-10
 * ```js
 * const el = mount('<ry-number-select min="0" max="100" step="5" value="50"></ry-number-select>');
 * const display = el.querySelector('[data-ry-target="display"]');
 * display.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowUp', shiftKey: true, bubbles: true }));
 * await expectEqual(el.value, 100, 'Shift+ArrowUp = 10× step (clamped at max)');
 * ```
 *
 * @tutorial home-and-end-jump-to-extremes
 * ```js
 * const el = mount('<ry-number-select min="0" max="100" value="42"></ry-number-select>');
 * const display = el.querySelector('[data-ry-target="display"]');
 * display.dispatchEvent(new KeyboardEvent('keydown', { key: 'End', bubbles: true }));
 * await expectEqual(el.value, 100);
 * display.dispatchEvent(new KeyboardEvent('keydown', { key: 'Home', bubbles: true }));
 * await expectEqual(el.value, 0);
 * ```
 *
 * @tutorial value-clamps-to-min-max
 * ```js
 * const el = mount('<ry-number-select min="0" max="10" step="1" value="9"></ry-number-select>');
 * const display = el.querySelector('[data-ry-target="display"]');
 * display.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowUp', bubbles: true }));
 * display.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowUp', bubbles: true }));
 * await expectEqual(el.value, 10, 'clamped at max');
 * display.dispatchEvent(new KeyboardEvent('keydown', { key: 'Home', bubbles: true }));
 * display.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowDown', bubbles: true }));
 * await expectEqual(el.value, 0, 'clamped at min');
 * ```
 *
 * @tutorial wrap-wraps-at-extremes
 * ```js
 * // Wrong assumption: every numeric stepper clamps at min/max. With [wrap],
 * // values past max wrap to min (and vice versa).
 * const el = mount('<ry-number-select min="0" max="3" step="1" value="3" wrap></ry-number-select>');
 * const display = el.querySelector('[data-ry-target="display"]');
 * display.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowUp', bubbles: true }));
 * // Range is max - min + step = 3 - 0 + 1 = 4 → wraps to 3 - 4 = -1, then +4 = 3
 * // Actually 3 + 1 = 4, wrap subtracts (3-0+1=4) → 4-4 = 0
 * await expectEqual(el.value, 0, 'wraps from max+1 to min');
 * ```
 *
 * @tutorial increment-button-fires-ry-change
 * ```js
 * const el = mount('<ry-number-select min="0" max="100" step="5" value="50"></ry-number-select>');
 * const inc = el.querySelector('[data-ry-target="increment"]');
 * const fired = [];
 * el.addEventListener('ry:change', (e) => fired.push(e.detail.value));
 * inc.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
 * inc.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
 * await expectEqual(el.value, 55);
 * await expectEqual(fired[0], 55, 'ry:change fires on button press');
 * ```
 *
 * @tutorial disabled-suppresses-keyboard
 * ```js
 * const el = mount('<ry-number-select min="0" max="100" value="50" disabled></ry-number-select>');
 * const display = el.querySelector('[data-ry-target="display"]');
 * const fired = [];
 * el.addEventListener('ry:change', () => fired.push(1));
 * display.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowUp', bubbles: true }));
 * await expectEqual(el.value, 50);
 * await expectEqual(fired.length, 0);
 * ```
 *
 * @tutorial aria-attributes-mirror-state
 * ```js
 * const el = mount('<ry-number-select min="0" max="100" value="42"></ry-number-select>');
 * const display = el.querySelector('[data-ry-target="display"]');
 * await expectAttr(display, 'role', 'spinbutton');
 * await expectAttr(display, 'tabindex', '0');
 * await expectAttr(display, 'aria-valuenow', '42');
 * await expectAttr(display, 'aria-valuemin', '0');
 * await expectAttr(display, 'aria-valuemax', '100');
 * el.value = 75;
 * // attributeChangedCallback fires updateDisplay which re-applies aria
 * await expectAttr(display, 'aria-valuenow', '75');
 * ```
 *
 * @tutorial beforechange-veto
 * ```js
 * // preventDefault() on ry:beforechange keeps the current value and ry:change
 * // stays silent — e.g. "don't let quantity exceed stock".
 * const el = mount('<ry-number-select min="0" max="100" step="5" value="50"></ry-number-select>');
 * const display = el.querySelector('[data-ry-target="display"]');
 * const changed = [];
 * el.addEventListener('ry:change', (e) => changed.push(e.detail.value));
 * el.addEventListener('ry:beforechange', (e) => {
 *   if (e.detail.value === 55 && e.detail.previous === 50) e.preventDefault();
 * }, { once: true });
 * display.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowUp', bubbles: true }));
 * await expectEqual(el.value, 50, 'vetoed step leaves the value at 50');
 * await expectEqual(changed.length, 0, 'ry:change did not fire');
 * display.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowUp', bubbles: true }));
 * await expectEqual(el.value, 55, 'without the veto, the step lands');
 * await expectEqual(changed[0], 55, 'ry:change fired with the new value');
 * ```
 *
 * @see ry-slider
 * @see ry-knob
 */

import { RyElement } from '../core/ry-element.js';
import { getIcon } from '../core/ry-icons.js';

const DRAG_PX_PER_STEP = 15;
const CLICK_THRESHOLD = 3;

export class RyNumberSelect extends RyElement {
  #display: HTMLElement | null = null;
  #valueEl: HTMLElement | null = null;
  #inputEl: HTMLInputElement | null = null;
  #decrementBtn: HTMLElement | null = null;
  #incrementBtn: HTMLElement | null = null;

  #dragging = false;
  #startX = 0;
  #startY = 0;
  #startValue = 0;
  #editing = false;

  #repeatTimer: ReturnType<typeof setTimeout> | null = null;
  #repeatInterval: ReturnType<typeof setTimeout> | null = null;

  static observedAttributes = ['min', 'max', 'step', 'value', 'disabled', 'arrows', 'icons', 'drag', 'editable', 'wrap', 'label', 'prefix', 'suffix'] as const;

  setup(): void {
    this.#buildMarkup();
    this.#cacheRefs();
    this.#bindEvents();
    this.#updateDisplay();
  }

  #buildMarkup(): void {
    const arrows = this.getAttribute('arrows') ?? 'both';
    const label = this.getAttribute('label');
    const stacked = arrows === 'stacked' || arrows === 'stacked-end' || arrows === 'stacked-start';

    const [decIcon, incIcon] = this.#getIcons(stacked);

    const showDecrement = arrows !== 'end' && arrows !== 'none';
    const showIncrement = arrows !== 'start' && arrows !== 'none';
    const prefix = this.getAttribute('prefix');
    const suffix = this.getAttribute('suffix');

    const decBtn = showDecrement
      ? `<button data-ry-target="decrement" class="ry-number-select__btn ry-number-select__decrement" aria-label="Decrease" tabindex="-1" type="button">${decIcon}</button>`
      : '';
    const incBtn = showIncrement
      ? `<button data-ry-target="increment" class="ry-number-select__btn ry-number-select__increment" aria-label="Increase" tabindex="-1" type="button">${incIcon}</button>`
      : '';
    const display = `
      <div data-ry-target="display" class="ry-number-select__display" tabindex="0" role="spinbutton"
           aria-valuenow="${this.value}" aria-valuemin="${this.min}" aria-valuemax="${this.max}"
           ${label ? `aria-label="${label}"` : ''}>
        ${prefix ? `<span data-ry-target="prefix" class="ry-number-select__affix">${prefix}</span>` : ''}
        <span data-ry-target="value" class="ry-number-select__value">${this.#formatValue(this.value)}</span>
        ${suffix ? `<span data-ry-target="suffix" class="ry-number-select__affix">${suffix}</span>` : ''}
        <input data-ry-target="input" class="ry-number-select__input" type="text" inputmode="decimal" tabindex="-1">
      </div>`;
    const labelEl = label ? `<span data-ry-target="label" class="ry-number-select__label">${label}</span>` : '';

    if (arrows === 'stacked') {
      // Vertical: increment on top, display in middle, decrement on bottom
      this.innerHTML = `${incBtn}${display}${decBtn}${labelEl}`;
    } else if (arrows === 'stacked-end') {
      // Horizontal: display, then stacked buttons on the right
      const group = `<div data-ry-target="btn-group" class="ry-number-select__btn-group">${incBtn}${decBtn}</div>`;
      this.innerHTML = `${display}${group}${labelEl}`;
    } else if (arrows === 'stacked-start') {
      // Horizontal: stacked buttons on the left, then display
      const group = `<div data-ry-target="btn-group" class="ry-number-select__btn-group">${incBtn}${decBtn}</div>`;
      this.innerHTML = `${group}${display}${labelEl}`;
    } else {
      this.innerHTML = `${decBtn}${display}${incBtn}${labelEl}`;
    }
  }

  #getIcons(stacked: boolean): [string, string] {
    const icons = this.getAttribute('icons') ?? 'plus-minus';

    if (icons === 'chevron') {
      if (stacked) {
        return [getIcon('chevron-down') ?? '▾', getIcon('chevron-up') ?? '▴'];
      }
      return [getIcon('chevron-left') ?? '‹', getIcon('chevron-right') ?? '›'];
    }

    if (icons === 'arrow') {
      if (stacked) {
        return [getIcon('chevron-down') ?? '▼', getIcon('chevron-up') ?? '▲'];
      }
      return [getIcon('chevron-left') ?? '◀', getIcon('chevron-right') ?? '▶'];
    }

    // Default: plus-minus
    return [getIcon('minus') ?? '−', getIcon('plus') ?? '+'];
  }

  #cacheRefs(): void {
    this.#display = this.$('[data-ry-target="display"]');
    this.#valueEl = this.$('[data-ry-target="value"]');
    this.#inputEl = this.$<HTMLInputElement>('[data-ry-target="input"]');
    this.#decrementBtn = this.$('[data-ry-target="decrement"]');
    this.#incrementBtn = this.$('[data-ry-target="increment"]');
  }

  #bindEvents(): void {
    // Buttons
    if (this.#decrementBtn) {
      this.on(this.#decrementBtn, 'mousedown', (e: MouseEvent) => { e.preventDefault(); this.#handleButtonDown(-1); });
      this.on(this.#decrementBtn, 'mouseup', () => this.#handleButtonUp());
      this.on(this.#decrementBtn, 'mouseleave', () => this.#handleButtonUp());
    }
    if (this.#incrementBtn) {
      this.on(this.#incrementBtn, 'mousedown', (e: MouseEvent) => { e.preventDefault(); this.#handleButtonDown(1); });
      this.on(this.#incrementBtn, 'mouseup', () => this.#handleButtonUp());
      this.on(this.#incrementBtn, 'mouseleave', () => this.#handleButtonUp());
    }

    // Drag on display
    if (this.#display) {
      this.on(this.#display, 'mousedown', this.#handleMouseDown);
    }
    this.on(document, 'mousemove', this.#handleMouseMove);
    this.on(document, 'mouseup', this.#handleMouseUp);

    // Touch
    if (this.#display) {
      this.on(this.#display, 'touchstart', this.#handleTouchStart);
    }
    this.on(document, 'touchmove', this.#handleTouchMove);
    this.on(document, 'touchend', this.#handleTouchEnd);

    // Keyboard
    if (this.#display) {
      this.on(this.#display, 'keydown', this.#handleKeydown);
    }

    // Wheel
    this.on(this, 'wheel', this.#handleWheel);

    // Double-click always enters edit mode
    if (this.#display) {
      this.on(this.#display, 'dblclick', () => this.#enterEditMode());
    }

    // Edit mode
    if (this.#inputEl) {
      this.on(this.#inputEl, 'blur', () => this.#commitEdit());
      this.on(this.#inputEl, 'keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter') { e.preventDefault(); this.#commitEdit(); }
        if (e.key === 'Escape') { e.preventDefault(); this.#cancelEdit(); }
        e.stopPropagation();
      });
    }
  }

  // --- Button hold-to-repeat ---

  #handleButtonDown(direction: 1 | -1): void {
    if (this.disabled) return;

    const step = this.step || 1;
    const next = this.#resolveStep(direction * step);
    if (!this.#beforeChange(next)) return;
    this.#applyValue(next);
    this.#emitChange();

    this.#repeatTimer = setTimeout(() => {
      let interval = 150;
      const tick = (): void => {
        this.#adjustValue(direction * step);
        this.#emitInput();
        interval = Math.max(40, interval * 0.85);
        this.#repeatInterval = setTimeout(tick, interval);
      };
      tick();
    }, 400);
  }

  #handleButtonUp(): void {
    if (this.#repeatTimer) { clearTimeout(this.#repeatTimer); this.#repeatTimer = null; }
    if (this.#repeatInterval) { clearTimeout(this.#repeatInterval); this.#repeatInterval = null; }
  }

  // --- Drag helpers ---

  get #dragAxis(): 'x' | 'y' | 'none' {
    const val = this.getAttribute('drag');
    if (val === 'x' || val === 'y' || val === 'none') return val;
    // Default: stacked layouts → vertical drag
    const arrows = this.getAttribute('arrows');
    if (arrows === 'stacked' || arrows === 'stacked-end' || arrows === 'stacked-start') return 'y';
    return 'x';
  }

  #getDragDelta(clientX: number, clientY: number): number {
    const axis = this.#dragAxis;
    if (axis === 'y') return this.#startY - clientY; // up = increase
    return clientX - this.#startX; // right = increase
  }

  #getDragMoved(clientX: number, clientY: number): number {
    const axis = this.#dragAxis;
    if (axis === 'y') return Math.abs(clientY - this.#startY);
    return Math.abs(clientX - this.#startX);
  }

  #applyMicroOffset(remainder: number, pxPerStep: number): void {
    if (!this.#valueEl) return;
    const offset = (remainder / pxPerStep) * 4;
    const axis = this.#dragAxis;
    this.#valueEl.style.transform = axis === 'y'
      ? `translateY(${-offset}px)`
      : `translateX(${offset}px)`;
  }

  #processDrag(clientX: number, clientY: number, shiftKey: boolean): void {
    const d = this.#getDragDelta(clientX, clientY);
    const step = this.step || 1;
    const effectiveStep = shiftKey ? step * 10 : step;
    const pxPerStep = shiftKey ? DRAG_PX_PER_STEP * 2 : DRAG_PX_PER_STEP;
    const steps = Math.round(d / pxPerStep);
    const newVal = this.#startValue + steps * effectiveStep;

    const remainder = d - steps * pxPerStep;
    this.#applyMicroOffset(remainder, pxPerStep);

    const clamped = this.hasAttribute('wrap') ? this.#wrapValue(newVal) : this.#clamp(newVal);
    const snapped = this.#snapToStep(clamped);

    if (snapped !== this.value) {
      this.value = snapped;
      this.#updateDisplay();
      this.#emitInput();
    }
  }

  // --- Mouse drag ---

  #handleMouseDown = (e: MouseEvent): void => {
    if (this.disabled || this.#editing || this.#dragAxis === 'none') return;
    e.preventDefault();
    this.#dragging = true;
    this.#startX = e.clientX;
    this.#startY = e.clientY;
    this.#startValue = this.value;
    this.setAttribute('data-dragging', '');
  };

  #handleMouseMove = (e: MouseEvent): void => {
    if (!this.#dragging) return;
    this.#processDrag(e.clientX, e.clientY, e.shiftKey);
  };

  #handleMouseUp = (e: MouseEvent): void => {
    if (!this.#dragging) return;
    const moved = this.#getDragMoved(e.clientX, e.clientY);
    this.#dragging = false;
    this.removeAttribute('data-dragging');

    if (this.#valueEl) {
      this.#valueEl.style.transform = '';
    }

    if (moved < CLICK_THRESHOLD && this.hasAttribute('editable')) {
      this.#enterEditMode();
    } else if (moved >= CLICK_THRESHOLD) {
      this.#commitDrag();
    }
  };

  /**
   * Drag release: the value was already moved live (ry:input per frame), so
   * the before-event describes the commit of that value against the pre-drag
   * one. A veto snaps back to where the drag started.
   */
  #commitDrag(): void {
    const previous = this.#startValue;
    if (!this.emit('beforechange', { value: this.value, previous })) {
      this.#applyValue(previous);
      return;
    }
    this.#emitChange();
  }

  // --- Touch drag ---

  #handleTouchStart = (e: TouchEvent): void => {
    if (this.disabled || this.#editing || this.#dragAxis === 'none') return;
    e.preventDefault();
    this.#dragging = true;
    this.#startX = e.touches[0]?.clientX ?? 0;
    this.#startY = e.touches[0]?.clientY ?? 0;
    this.#startValue = this.value;
    this.setAttribute('data-dragging', '');
  };

  #handleTouchMove = (e: TouchEvent): void => {
    if (!this.#dragging) return;
    e.preventDefault();
    const touch = e.touches[0];
    if (!touch) return;
    this.#processDrag(touch.clientX, touch.clientY, false);
  };

  #handleTouchEnd = (): void => {
    if (!this.#dragging) return;
    this.#dragging = false;
    this.removeAttribute('data-dragging');

    if (this.#valueEl) {
      this.#valueEl.style.transform = '';
    }

    this.#commitDrag();
  };

  // --- Keyboard ---

  #handleKeydown = (e: KeyboardEvent): void => {
    if (this.disabled || this.#editing) return;

    const step = this.step || 1;
    const largeStep = step * 10;
    let delta = 0;

    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowRight':
        delta = e.shiftKey ? largeStep : step;
        break;
      case 'ArrowDown':
      case 'ArrowLeft':
        delta = -(e.shiftKey ? largeStep : step);
        break;
      case 'PageUp':
        delta = largeStep;
        break;
      case 'PageDown':
        delta = -largeStep;
        break;
      case 'Home':
        delta = this.min - this.value;
        break;
      case 'End':
        delta = this.max - this.value;
        break;
      case 'Enter':
        if (this.hasAttribute('editable')) {
          this.#enterEditMode();
        }
        return;
      default:
        if (this.hasAttribute('editable') && /^[0-9.\-]$/.test(e.key)) {
          this.#enterEditMode(e.key);
          e.preventDefault();
        }
        return;
    }

    e.preventDefault();
    this.#stepAndCommit(delta);
  };

  // --- Wheel ---

  #handleWheel = (e: WheelEvent): void => {
    if (this.disabled || this.#editing) return;
    if (!this.matches(':hover') && !this.contains(document.activeElement)) return;

    e.preventDefault();
    const step = this.step || 1;
    const effectiveStep = e.shiftKey ? step * 10 : step;
    const delta = e.deltaY < 0 ? effectiveStep : -effectiveStep;
    this.#stepAndCommit(delta);
  };

  /** Keyboard / wheel step: resolve → beforechange → apply → input + change. */
  #stepAndCommit(delta: number): void {
    const next = this.#resolveStep(delta);
    if (!this.#beforeChange(next)) return;
    this.#applyValue(next);
    this.#emitInput();
    this.#emitChange();
  }

  #beforeChange(next: number): boolean {
    return this.emit('beforechange', { value: next, previous: this.value });
  }

  // --- Edit mode ---

  #enterEditMode(initialChar?: string): void {
    if (this.disabled || this.#editing) return;
    this.#editing = true;
    this.state = 'editing';

    if (this.#valueEl) this.#valueEl.style.display = 'none';
    if (this.#inputEl) {
      this.#inputEl.style.display = 'block';
      this.#inputEl.value = initialChar ?? this.#formatValue(this.value);
      this.#inputEl.focus();
      if (initialChar) {
        this.#inputEl.setSelectionRange(1, 1);
      } else {
        this.#inputEl.select();
      }
    }
  }

  #commitEdit(): void {
    if (!this.#editing) return;
    this.#editing = false;
    this.state = '';

    const raw = parseFloat(this.#inputEl?.value ?? '');
    if (!isNaN(raw)) {
      const clamped = this.#clamp(raw);
      const snapped = this.#snapToStep(clamped);
      if (this.#beforeChange(snapped)) {
        this.value = snapped;
        this.#emitChange();
      }
    }

    if (this.#valueEl) this.#valueEl.style.display = '';
    if (this.#inputEl) this.#inputEl.style.display = 'none';
    this.#updateDisplay();
    this.#display?.focus();
  }

  #cancelEdit(): void {
    if (!this.#editing) return;
    this.#editing = false;
    this.state = '';

    if (this.#valueEl) this.#valueEl.style.display = '';
    if (this.#inputEl) this.#inputEl.style.display = 'none';
    this.#display?.focus();
  }

  // --- Value management ---

  #adjustValue(delta: number): void {
    this.#applyValue(this.#resolveStep(delta));
  }

  /** Where a `delta` step lands (wrap/clamp + snap) — pure, does not apply. */
  #resolveStep(delta: number): number {
    let newVal = this.value + delta;

    if (this.hasAttribute('wrap')) {
      newVal = this.#wrapValue(newVal);
    } else {
      newVal = this.#clamp(newVal);
    }

    return this.#snapToStep(newVal);
  }

  #applyValue(val: number): void {
    this.value = val;
    this.#updateDisplay();
  }

  #clamp(val: number): number {
    return Math.max(this.min, Math.min(this.max, val));
  }

  #wrapValue(val: number): number {
    const step = this.step || 1;
    const range = this.max - this.min + step;
    let wrapped = val;
    while (wrapped > this.max) wrapped -= range;
    while (wrapped < this.min) wrapped += range;
    return wrapped;
  }

  #snapToStep(val: number): number {
    const step = this.step;
    if (step <= 0) return this.#clamp(val);
    const snapped = Math.round((val - this.min) / step) * step + this.min;
    return this.#clamp(snapped);
  }

  #formatValue(val: number): string {
    const step = this.step;
    if (step >= 1) return Math.round(val).toString();
    const decimals = Math.max(0, -Math.floor(Math.log10(step)));
    return val.toFixed(decimals);
  }

  #updateDisplay(): void {
    if (this.#valueEl) {
      this.#valueEl.textContent = this.#formatValue(this.value);
    }
    this.#updateAria();
  }

  #updateAria(): void {
    if (this.#display) {
      this.#display.setAttribute('aria-valuenow', String(this.value));
      this.#display.setAttribute('aria-valuemin', String(this.min));
      this.#display.setAttribute('aria-valuemax', String(this.max));
    }
  }

  #emitInput(): void {
    this.emit('input', { value: this.value });
  }

  #emitChange(): void {
    this.emit('change', { value: this.value });
  }

  // --- Public API ---

  get value(): number {
    return parseFloat(this.getAttribute('value') ?? '0');
  }
  set value(val: number) {
    this.setAttribute('value', String(val));
  }

  get min(): number {
    return parseFloat(this.getAttribute('min') ?? '0');
  }
  set min(val: number) {
    this.setAttribute('min', String(val));
  }

  get max(): number {
    return parseFloat(this.getAttribute('max') ?? '100');
  }
  set max(val: number) {
    this.setAttribute('max', String(val));
  }

  get step(): number {
    return parseFloat(this.getAttribute('step') ?? '1');
  }
  set step(val: number) {
    this.setAttribute('step', String(val));
  }

  get drag(): 'x' | 'y' | 'none' {
    return this.#dragAxis;
  }
  set drag(val: 'x' | 'y' | 'none') {
    this.setAttribute('drag', val);
  }

  get disabled(): boolean {
    return this.hasAttribute('disabled');
  }
  set disabled(val: boolean) {
    if (val) this.setAttribute('disabled', '');
    else this.removeAttribute('disabled');
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (name === 'value' && this.#display) {
      this.#updateDisplay();
    }
  }

  teardown(): void {
    this.#handleButtonUp();
  }
}

customElements.define('ry-number-select', RyNumberSelect);
